<template>
  <div id="landing-plan-form-page">
    <Loader :active="loading"></Loader>

    <div class="form-container">
      <div class="page-header">
        <div class="page-header-content">
          <h1 class="title has-text-primary">
            {{ isEdit ? "Editar plan" : "Nuevo plan" }}
          </h1>

          <p class="page-description">
            {{
              isEdit
                ? "Modifica la información del plan"
                : "Completa la información del plan"
            }}
          </p>
        </div>

        <b-button
          type="is-light"
          icon-pack="fas"
          icon-left="arrow-left"
          native-type="button"
          @click="goBack"
        >
          Volver
        </b-button>
      </div>

      <form @submit.prevent="savePlan">
        <!-- INFORMACIÓN -->
        <section class="form-section">
          <h2 class="section-title">Información</h2>

          <b-field
            label="Nombre"
            :type="hasError('name') ? 'is-danger' : ''"
            :message="getError('name')"
          >
            <b-input
              v-model.trim="form.name"
              placeholder="Escribe el nombre del plan"
              maxlength="255"
              required
            ></b-input>
          </b-field>

          <b-field
            class="image-field"
            label="Imagen del plan"
            :type="hasError('image') ? 'is-danger' : ''"
            :message="getError('image')"
          >
            <div class="plan-upload">
              <label class="button is-primary is-outlined plan-upload-button">
                <b-icon pack="fas" icon="upload" size="is-small"></b-icon>

                <span>Seleccionar archivo</span>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  class="plan-upload-input"
                  @change="handleImageChange"
                />
              </label>
            </div>

            <p class="help">
              Formatos permitidos: JPG, JPEG, PNG y WEBP. Tamaño máximo: 5 MB.
            </p>
          </b-field>

          <div
            v-if="imagePreview"
            class="plan-image-preview"
            style="width: 240px; height: 160px; overflow: hidden"
          >
            <img
              :src="imagePreview"
              alt="Imagen del plan"
              style="
                width: 100%;
                height: 100%;
                object-fit: contain;
                display: block;
              "
            />
          </div>

          <b-field
            label="Tipo de plan"
            :type="hasError('type') ? 'is-danger' : ''"
            :message="getError('type')"
          >
            <b-select
              v-model="form.type"
              placeholder="Selecciona el tipo de plan"
              expanded
              required
            >
              <option
                v-for="type in planTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </b-select>

            <p v-if="hasLegacyType" class="help is-warning legacy-type-help">
              El tipo anterior "{{ originalType }}" ya no está disponible.
              Seleccioná una de las opciones actuales antes de guardar.
            </p>
          </b-field>

          <b-field
            label="Descripción"
            :type="hasError('description') ? 'is-danger' : ''"
            :message="getError('description')"
          >
            <RichTextEditor
              v-model="form.description"
              placeholder="Escribe una descripción general del plan"
              :min-height="200"
              :has-error="hasError('description')"
            />
          </b-field>

          <!-- BENEFICIOS -->
          <div class="benefits-field">
            <label class="label">Beneficios</label>

            <div class="benefit-input-row">
              <div class="benefit-input">
                <b-autocomplete
                  v-model="newBenefit"
                  :data="filteredBenefitOptions"
                  field="title"
                  placeholder="Escribe el nombre del beneficio"
                  maxlength="255"
                  @keyup.native.enter.prevent="addBenefit"
                  @keydown.native.188.prevent="addBenefit"
                  @select="selectBenefit"
                >
                  <template slot-scope="props">
                    {{ props.option.title }}
                  </template>
                  <template slot="empty">No hay beneficios coincidentes</template>
                </b-autocomplete>
              </div>

              <b-button
                type="is-primary"
                icon-pack="fas"
                icon-left="plus"
                native-type="button"
                @click="addBenefit"
              >
                Agregar
              </b-button>
            </div>

            <p class="help">
              Selecciona uno existente o crea uno nuevo con Enter o coma. Arrastra
              los tags para cambiar su orden.
            </p>

            <div v-if="form.benefits.length === 0" class="empty-list">
              No se agregaron beneficios todavía.
            </div>

            <draggable
              v-else
              v-model="form.benefits"
              class="tags-list"
              handle=".tag-drag-handle"
              ghost-class="tag-ghost"
              @end="updateBenefitOrder"
            >
              <div
                v-for="(benefit, index) in form.benefits"
                :key="benefit._key || benefit.benefit_id || benefit.id"
                class="custom-tag"
              >
                <span class="tag-drag-handle" title="Arrastrar para reordenar">
                  <i class="fas fa-grip-vertical"></i>
                </span>
                <span>{{ benefit.title }}</span>

                <button
                  type="button"
                  class="tag-remove"
                  title="Eliminar beneficio"
                  @click="removeBenefit(index)"
                >
                  ×
                </button>
              </div>
            </draggable>

            <p v-if="hasError('benefits')" class="help is-danger">
              {{ getError("benefits") }}
            </p>
          </div>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar plan en el sitio web </span>

              <p class="switch-description">
                Permite mostrar u ocultar este plan en la web.
              </p>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_on_website ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_on_website"
                type="is-success"
              ></b-switch>
            </div>
          </div>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar plan en la landing de ventas </span>

              <p class="switch-description">
                Permite mostrar u ocultar este plan en la landing de ventas.
              </p>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_in_sales ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_in_sales"
                type="is-success"
              ></b-switch>
            </div>
          </div>
        </section>

        <!-- LANDING BIENVENIDA -->
        <section class="form-section">
          <h2 class="section-title">Landing Bienvenida</h2>

          <b-field
            label="Título de la landing"
            :type="hasError('landing_title') ? 'is-danger' : ''"
            :message="getError('landing_title')"
          >
            <b-input
              v-model.trim="form.landing_title"
              placeholder="Escribe el título principal de la landing"
              maxlength="255"
            ></b-input>
          </b-field>

          <b-field
            label="Contenido"
            :type="hasError('welcome_content') ? 'is-danger' : ''"
            :message="getError('welcome_content')"
          >
            <RichTextEditor
              v-model="form.welcome_content"
              placeholder="Escribe el contenido de bienvenida del plan"
              :min-height="260"
              :has-error="hasError('welcome_content')"
            />
          </b-field>
          <!-- SERVICIOS APP -->
          <div class="benefits-field">
            <label class="label"> Servicios incluidos en la APP </label>

            <div class="benefit-input-row">
              <div class="benefit-input">
                <b-input
                  v-model.trim="newAppService"
                  placeholder="Escribe el nombre del servicio"
                  maxlength="255"
                  @keyup.native.enter.prevent="addAppService"
                ></b-input>
              </div>

              <b-button
                type="is-primary"
                icon-pack="fas"
                icon-left="plus"
                native-type="button"
                @click="addAppService"
              >
                Agregar
              </b-button>
            </div>

            <div v-if="form.app_services.length === 0" class="empty-list">
              No se agregaron servicios de la APP.
            </div>

            <div v-else class="tags-list">
              <div
                v-for="(service, index) in form.app_services"
                :key="'service-' + index"
                class="custom-tag"
              >
                <span>{{ service }}</span>

                <button
                  type="button"
                  class="tag-remove"
                  title="Eliminar servicio"
                  @click="removeAppService(index)"
                >
                  ×
                </button>
              </div>
            </div>

            <p v-if="hasError('app_services')" class="help is-danger">
              {{ getError("app_services") }}
            </p>
          </div>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar información de la APP </span>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_app_information ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_app_information"
                type="is-success"
              ></b-switch>
            </div>
          </div>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar medios de pago </span>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_payment_methods ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_payment_methods"
                type="is-success"
              ></b-switch>
            </div>
          </div>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar WhatsApp exclusivo </span>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_whatsapp_exclusive ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_whatsapp_exclusive"
                type="is-success"
              ></b-switch>
            </div>
          </div>
        </section>

        <!-- CARTILLA -->
        <section class="form-section">
          <h2 class="section-title">Cartilla de Prestadores</h2>

          <b-field
            label="ID o código del plan"
            :type="hasError('cartilla_plan_id') ? 'is-danger' : ''"
            :message="getError('cartilla_plan_id')"
          >
            <b-input
              v-model.trim="form.cartilla_plan_id"
              placeholder="Escribe el ID o código del plan"
              maxlength="100"
            ></b-input>
          </b-field>

          <p class="help cartilla-help">
            Este código se utilizará para vincular el plan con la cartilla de
            prestadores.
          </p>

          <div class="switch-row">
            <div>
              <span class="switch-title"> Mostrar plan en la cartilla </span>
            </div>

            <div class="switch-control">
              <span class="switch-value">
                {{ form.show_in_cartilla ? "Sí" : "No" }}
              </span>

              <b-switch
                v-model="form.show_in_cartilla"
                type="is-success"
              ></b-switch>
            </div>
          </div>
        </section>

        <div class="form-actions">
          <b-button
            type="is-primary"
            icon-pack="fas"
            icon-left="save"
            native-type="submit"
            :loading="loading"
            :disabled="loading"
          >
            {{ isEdit ? "Actualizar" : "Guardar" }}
          </b-button>

          <b-button
            type="is-light"
            native-type="button"
            :disabled="loading"
            @click="goBack"
          >
            Cancelar
          </b-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/Loader.vue";
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import { alertFailed, alertSuccess } from "@/helpers";
import RichTextEditor from "@/components/RichTextEditor.vue";
import draggable from "vuedraggable";
import {
  LANDING_PLAN_TYPES,
  LANDING_PLAN_TYPE_VALUES,
} from "@/constants/landingPlanTypes";

export default {
  name: "LandingPlanForm",

  components: {
    Loader,
    EditorContent,
    RichTextEditor,
    draggable,
  },

  data() {
    return {
      loading: false,
      errors: {},
      descriptionEditor: null,
      welcomeEditor: null,
      currentImage: "",
      imagePreview: "",

      newBenefit: "",
      benefitOptions: [],
      selectedBenefit: null,
      newAppService: "",
      planTypes: LANDING_PLAN_TYPES,
      originalType: "",

      form: {
        name: "",
        type: "",
        image: null,
        description: "",
        benefits: [
          {
            title: "Cartilla de prestadores equilibrada y accesible.",
            description: "",
            sort_order: 1,
            is_active: true,
          },
          {
            title: "Estudios de baja, media y alta complejidad con ACP.",
            description: "",
            sort_order: 2,
            is_active: true,
          },
          {
            title: "Odontología general con ACP.",
            description: "",
            sort_order: 3,
            is_active: true,
          },
          {
            title: "100% de cobertura en internaciones clínicas y quirúrgicas.",
            description: "",
            sort_order: 4,
            is_active: true,
          },
          {
            title: "Emergencias y urgencias.",
            description: "",
            sort_order: 5,
            is_active: true,
          },
          {
            title: "Consulta Médica Online a través de UMA Salud.",
            description: "",
            sort_order: 6,
            is_active: true,
          },
        ],
        show_on_website: true,
        show_in_sales: false,

        landing_title: "",
        welcome_content: "",
        app_services: [
          "Credencial Digital",
          "Nueva Orden",
          "Cartilla de Prestadores",
          "Historial, prácticas médicas y formularios",
          "Cupón de Pago",
          "Buscar Delegación más cercana",
        ],
        show_app_information: true,
        show_payment_methods: true,
        show_whatsapp_exclusive: true,

        cartilla_plan_id: "",
        show_in_cartilla: true,

        status: "published",
        sort_order: 0,
      },
    };
  },

  computed: {
    filteredBenefitOptions() {
      const search = this.newBenefit.trim().toLocaleLowerCase();
      const selectedIds = this.form.benefits.map((benefit) => benefit.benefit_id);

      return this.benefitOptions.filter((benefit) => {
        return (
          !selectedIds.includes(benefit.id) &&
          (search === "" || benefit.title.toLocaleLowerCase().includes(search))
        );
      });
    },

    isEdit() {
      return Boolean(this.$route.params.id);
    },

    hasLegacyType() {
      return Boolean(
        this.originalType &&
          !LANDING_PLAN_TYPE_VALUES.includes(this.originalType) &&
          !LANDING_PLAN_TYPE_VALUES.includes(this.form.type)
      );
    },

    imageName() {
      if (this.form.image instanceof File) {
        return this.form.image.name;
      }

      if (this.currentImage) {
        const cleanUrl = this.currentImage.split("?")[0];
        const parts = cleanUrl.split("/");

        return decodeURIComponent(parts[parts.length - 1] || "Imagen actual");
      }

      return "Ningún archivo seleccionado";
    },
  },

  methods: {
    normalizeBoolean(value, defaultValue = false) {
      if (value === true || value === 1 || value === "1" || value === "true") {
        return true;
      }

      if (
        value === false ||
        value === 0 ||
        value === "0" ||
        value === "false" ||
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return false;
      }

      return defaultValue;
    },

    normalizeArray(value) {
      if (Array.isArray(value)) {
        return value;
      }

      if (typeof value === "string" && value.trim() !== "") {
        try {
          const parsed = JSON.parse(value);

          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error("No se pudo convertir el valor en array:", error);

          return [];
        }
      }

      return [];
    },

    normalizeBenefits(value) {
      const benefits = this.normalizeArray(value);

      return benefits.map((benefit, index) => {
        if (typeof benefit === "string") {
          return {
            _key: `new-${index}-${benefit}`,
            title: benefit,
            description: "",
            sort_order: index + 1,
            is_active: true,
          };
        }

        return {
          _key: benefit.id ? `assignment-${benefit.id}` : `benefit-${benefit.benefit_id || index}`,
          id: benefit.id || null,
          benefit_id: benefit.benefit_id || null,
          title: benefit.title || "",
          description: benefit.description || "",
          sort_order: benefit.sort_order || index + 1,
          is_active: this.normalizeBoolean(benefit.is_active, true),
        };
      });
    },

    async fetchPlan() {
      try {
        this.loading = true;
        this.errors = {};

        const response = await this.$repositories.landingPlans.show(
          this.$route.params.id
        );

        const plan = response.data.data || response.data;

        this.currentImage = plan.image || "";
        this.imagePreview = plan.image || "";

        this.form.name = plan.name || "";
        this.originalType = plan.type || "";
        this.form.type = LANDING_PLAN_TYPE_VALUES.includes(this.originalType)
          ? this.originalType
          : "";
        this.form.image = null;
        this.form.description = plan.description || "";
        if (this.descriptionEditor) {
          this.descriptionEditor.commands.setContent(
            this.form.description,
            false
          );
        }

        this.form.benefits = this.normalizeBenefits(plan.benefits);

        this.form.show_on_website = this.normalizeBoolean(
          plan.show_on_website,
          true
        );

        this.form.show_in_sales = this.normalizeBoolean(
          plan.show_in_sales,
          false
        );

        this.form.landing_title = plan.landing_title || "";
        this.form.welcome_content = plan.welcome_content || "";
        if (this.welcomeEditor) {
          this.welcomeEditor.commands.setContent(
            this.form.welcome_content,
            false
          );
        }
        this.form.app_services = this.normalizeArray(plan.app_services);

        this.form.show_app_information = this.normalizeBoolean(
          plan.show_app_information,
          true
        );

        this.form.show_payment_methods = this.normalizeBoolean(
          plan.show_payment_methods,
          true
        );

        this.form.show_whatsapp_exclusive = this.normalizeBoolean(
          plan.show_whatsapp_exclusive,
          true
        );

        this.form.cartilla_plan_id = plan.cartilla_plan_id || "";

        this.form.show_in_cartilla = this.normalizeBoolean(
          plan.show_in_cartilla,
          true
        );

        this.form.status = plan.status || "published";

        this.form.sort_order =
          plan.sort_order !== null && plan.sort_order !== undefined
            ? Number(plan.sort_order)
            : 0;
      } catch (error) {
        console.error("Error cargando el plan:", error);

        alertFailed("No se pudo cargar el plan.");

        this.$router.push("/landing-planes");
      } finally {
        this.loading = false;
      }
    },

    async fetchBenefitOptions() {
      try {
        const response = await this.$repositories.landingPlans.benefits();
        this.benefitOptions = response.data.data || [];
      } catch (error) {
        console.error("No se pudo cargar el catálogo de beneficios:", error);
      }
    },

    handleImageChange(event) {
      const file = event.target.files[0] || null;

      if (this.imagePreview && this.imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.imagePreview);
      }

      this.form.image = file;

      if (file) {
        this.imagePreview = URL.createObjectURL(file);
      } else {
        this.imagePreview = this.currentImage;
      }
    },

    selectBenefit(benefit) {
      if (!benefit) {
        return;
      }

      this.selectedBenefit = benefit;
      this.addBenefit();
    },

    addBenefit() {
      const selected = this.selectedBenefit;
      const title = selected ? selected.title : this.newBenefit.replace(/,+$/, "").trim();

      if (title === "") {
        return;
      }

      const exists = this.form.benefits.some((benefit) => {
        return benefit.title.toLowerCase() === title.toLowerCase();
      });

      if (exists) {
        alertFailed("Ese beneficio ya fue agregado.");

        this.newBenefit = "";
        this.selectedBenefit = null;

        return;
      }

      this.form.benefits.push({
        _key: `new-${Date.now()}-${Math.random()}`,
        benefit_id: selected ? selected.id : null,
        title,
        description: selected ? selected.description || "" : "",
        sort_order: this.form.benefits.length + 1,
        is_active: true,
      });

      this.newBenefit = "";
      this.selectedBenefit = null;
    },

    removeBenefit(index) {
      this.form.benefits.splice(index, 1);

      this.form.benefits.forEach((benefit, benefitIndex) => {
        benefit.sort_order = benefitIndex + 1;
      });
    },

    updateBenefitOrder() {
      this.form.benefits.forEach((benefit, index) => {
        benefit.sort_order = index + 1;
      });
    },

    addAppService() {
      const service = this.newAppService.trim();

      if (service === "") {
        return;
      }

      const exists = this.form.app_services.some((item) => {
        return String(item).toLowerCase() === service.toLowerCase();
      });

      if (exists) {
        alertFailed("Ese servicio ya fue agregado.");

        return;
      }

      this.form.app_services.push(service);

      this.newAppService = "";
    },

    removeAppService(index) {
      this.form.app_services.splice(index, 1);
    },

    validateForm() {
      if (this.form.name.trim() === "") {
        alertFailed("Debes escribir el nombre del plan.");

        return false;
      }

      if (!LANDING_PLAN_TYPE_VALUES.includes(this.form.type)) {
        alertFailed("Debes seleccionar un tipo de plan válido.");

        return false;
      }

      return true;
    },

    buildFormData() {
      const data = new FormData();

      const benefits = this.form.benefits.map((benefit, index) => {
        return {
          id: benefit.id || null,
          benefit_id: benefit.benefit_id || null,
          title: benefit.title,
          description: benefit.description || "",
          sort_order: index + 1,
          is_active: benefit.is_active ? 1 : 0,
        };
      });

      data.append("name", this.form.name);
      data.append("type", this.form.type);

      data.append("description", this.form.description || "");

      data.append("welcome_content", this.form.welcome_content || "");

      data.append("show_on_website", this.form.show_on_website ? "1" : "0");
      data.append("show_in_sales", this.form.show_in_sales ? "1" : "0");

      data.append("cartilla_plan_id", this.form.cartilla_plan_id || "");

      data.append("status", this.form.status);

      data.append(
        "sort_order",
        this.form.sort_order !== "" ? String(this.form.sort_order) : "0"
      );

      data.append("benefits", JSON.stringify(benefits));

      data.append("landing_title", this.form.landing_title || "");

      data.append("app_services", JSON.stringify(this.form.app_services));

      data.append(
        "show_app_information",
        this.form.show_app_information ? "1" : "0"
      );

      data.append(
        "show_payment_methods",
        this.form.show_payment_methods ? "1" : "0"
      );

      data.append(
        "show_whatsapp_exclusive",
        this.form.show_whatsapp_exclusive ? "1" : "0"
      );

      data.append("show_in_cartilla", this.form.show_in_cartilla ? "1" : "0");

      if (this.form.image instanceof File) {
        data.append("image", this.form.image, this.form.image.name);
      }

      return data;
    },

    async savePlan() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.loading = true;
        this.errors = {};

        const data = this.buildFormData();

        let response;

        if (this.isEdit) {
          response = await this.$repositories.landingPlans.update(
            this.$route.params.id,
            data
          );
        } else {
          response = await this.$repositories.landingPlans.create(data);
        }

        alertSuccess(
          response.data.message ||
            (this.isEdit
              ? "Plan actualizado correctamente."
              : "Plan creado correctamente.")
        );

        this.$router.push("/landing-planes");
      } catch (error) {
        console.error("Error guardando el plan:", error);

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};

          alertFailed("Revisa los campos del formulario.");

          return;
        }

        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          alertFailed(error.response.data.message);

          return;
        }

        alertFailed(
          this.isEdit
            ? "No se pudo actualizar el plan."
            : "No se pudo crear el plan."
        );
      } finally {
        this.loading = false;
      }
    },

    hasError(field) {
      return Object.prototype.hasOwnProperty.call(this.errors, field);
    },

    getError(field) {
      if (!this.hasError(field)) {
        return "";
      }

      const error = this.errors[field];

      if (Array.isArray(error)) {
        return error[0];
      }

      return error;
    },

    goBack() {
      this.$router.push("/landing-planes");
    },
  },

  created() {
    this.fetchBenefitOptions();

    if (this.isEdit) {
      this.fetchPlan();
    }
  },

  mounted() {
    this.descriptionEditor = new Editor({
      content: this.form.description || "",

      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3],
          },
        }),
      ],

      onUpdate: ({ editor }) => {
        this.form.description = editor.getHTML();
      },
    });

    this.welcomeEditor = new Editor({
      content: this.form.welcome_content || "",

      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3],
          },
        }),
      ],

      onUpdate: ({ editor }) => {
        this.form.welcome_content = editor.getHTML();
      },
    });
  },

  beforeDestroy() {
    if (this.descriptionEditor) {
      this.descriptionEditor.destroy();
    }

    if (this.welcomeEditor) {
      this.welcomeEditor.destroy();
    }

    if (this.imagePreview && this.imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.imagePreview);
    }
  },
};
</script>

<style lang="scss" scoped>
.legacy-type-help {
  color: #a96500;
  font-weight: 600;
}

#landing-plan-form-page {
  position: relative;
  height: 100vh;
  overflow-y: auto;
  padding: 1.5rem 1.5rem 5rem;
  background: #f5f5f5;

  .form-container {
    width: 100%;
    max-width: 760px;
    margin: 0;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    min-height: 72px;
    margin-bottom: 1.25rem;
  }

  .page-header-content {
    display: block;
    flex: 1;
  }

  .page-header .title {
    display: block;
    margin: 0 0 0.35rem;
    padding: 0;
    line-height: 1.2;
  }

  .page-description {
    display: block;
    margin: 0;
    color: #7a7a7a;
    font-size: 0.95rem;
    line-height: 1.4;
  }

  .form-section {
    width: 100%;
    margin-bottom: 1rem;
    padding: 1.25rem;
    background: #ffffff;
    border: 1px solid #e4e4e4;
    border-top: 4px solid #0093bf;
    border-radius: 3px;
  }

  .section-title {
    margin: 0 0 1.25rem;
    padding-bottom: 0.6rem;
    color: #363636;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.25;
    border-bottom: 1px solid #ededed;
  }

  /*
  |--------------------------------------------------------------------------
  | Imagen
  |--------------------------------------------------------------------------
  */

  .plan-upload {
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  .plan-upload-name {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    min-width: 0;
    height: 40px;
    padding: 0 0.75rem;
    overflow: hidden;
    color: #7a7a7a;
    white-space: nowrap;
    text-overflow: ellipsis;
    background: #ffffff;
    border: 1px solid #dbdbdb;
    border-right: 0;
    border-radius: 4px 0 0 4px;
  }

  .plan-upload-button {
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: 40px;
    margin: 0;
    white-space: nowrap;
    cursor: pointer;
    border-radius: 4px;
  }

  .plan-upload-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
    border: 0;
  }

  .plan-image-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.75rem;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background: #f7f7f7;
    border: 1px solid #dddddd;
    border-radius: 4px;
  }

  /*
  |--------------------------------------------------------------------------
  | Editor enriquecido
  |--------------------------------------------------------------------------
  */

  .editor-box {
    display: block;
    width: 100%;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus-within {
      border-color: #0093bf;
      box-shadow: 0 0 0 1px #0093bf;
    }
  }

  .editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    min-height: 46px;
    padding: 0.5rem 0.65rem;
    background: #f5f5f5;
    border-bottom: 1px solid #dbdbdb;

    .button {
      min-width: 34px;
      height: 30px;
      margin: 0;
      padding-right: 0.6rem;
      padding-left: 0.6rem;
      box-shadow: none;
    }
  }

  .editor-divider {
    flex: 0 0 1px;
    width: 1px;
    height: 24px;
    margin: 0 0.2rem;
    background: #d0d0d0;
  }

  .editor-content {
    display: block;
    width: 100%;
    min-height: 200px;
    background: #ffffff;
    cursor: text;
  }

  .editor-content-large {
    min-height: 260px;
  }

  /*
  |--------------------------------------------------------------------------
  | Beneficios
  |--------------------------------------------------------------------------
  */

  .benefits-field {
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
  }

  .benefit-input-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .benefit-input {
    flex: 1;
  }

  .empty-list {
    margin-top: 0.9rem;
    padding: 0.9rem;
    color: #7a7a7a;
    text-align: center;
    background: #f7f7f7;
    border: 1px dashed #cccccc;
    border-radius: 4px;
  }

  .benefits-list {
    margin-top: 0.9rem;
  }

  .benefit-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 48px;
    margin-bottom: 0.5rem;
    padding: 0.55rem 0.75rem;
    background: #f8f8f8;
    border: 1px solid #dddddd;
    border-radius: 4px;
  }

  .benefit-number {
    display: flex;
    flex: 0 0 28px;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: #ffffff;
    font-size: 0.75rem;
    font-weight: 700;
    background: #0093bf;
    border-radius: 50%;
  }

  .benefit-content {
    flex: 1;
    min-width: 0;
  }

  .benefit-title {
    display: block;
    overflow: hidden;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /*
  |--------------------------------------------------------------------------
  | Etiquetas
  |--------------------------------------------------------------------------
  */

  .tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.9rem;
  }

  .custom-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 30px;
    padding: 0.25rem 0.4rem 0.25rem 0.75rem;
    font-size: 0.85rem;
    background: #eeeeee;
    border: 1px solid #d8d8d8;
    border-radius: 20px;
  }

  .tag-drag-handle {
    color: #999999;
    cursor: grab;
  }

  .tag-drag-handle:active {
    cursor: grabbing;
  }

  .tag-ghost {
    opacity: 0.35;
  }

  .tag-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    color: #ffffff;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    background: #f14668;
    border: 0;
    border-radius: 50%;
  }

  /*
  |--------------------------------------------------------------------------
  | Switches
  |--------------------------------------------------------------------------
  */

  .switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: 58px;
    padding: 0.7rem 0;
    border-top: 1px solid #e5e5e5;
  }

  .switch-title {
    display: block;
    font-weight: 600;
  }

  .switch-description {
    margin-top: 0.2rem;
    color: #7a7a7a;
    font-size: 0.8rem;
  }

  .switch-control {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
  }

  .switch-value {
    min-width: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    text-align: right;
  }

  .cartilla-help {
    margin-top: -0.5rem;
    margin-bottom: 1rem;
  }

  /*
  |--------------------------------------------------------------------------
  | Acciones
  |--------------------------------------------------------------------------
  */

  .form-actions {
    position: static;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    margin-top: 0;
    margin-bottom: 3rem;
    padding: 1rem;
    background: #ffffff;
    border: 1px solid #dedede;
    border-radius: 3px;
    box-shadow: none;
  }
}

/*
|--------------------------------------------------------------------------
| Componentes internos de Buefy
|--------------------------------------------------------------------------
*/

::v-deep .image-field .control {
  display: block !important;
  width: 100% !important;
}

::v-deep .image-field .help {
  display: block !important;
  width: 100% !important;
  margin-top: 0.4rem !important;
}

/*
|--------------------------------------------------------------------------
| Contenido interno de Tiptap
|--------------------------------------------------------------------------
*/

::v-deep .editor-content .ProseMirror {
  display: block;
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  color: #363636;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  background: #ffffff;
  border: 0;
  outline: none;
  box-shadow: none;
  box-sizing: border-box;
  cursor: text;

  p {
    margin: 0 0 0.75rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  h2 {
    margin: 0.75rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
  }

  h3 {
    margin: 0.75rem 0;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.3;
  }

  ul,
  ol {
    margin: 0.75rem 0;
    padding-left: 1.75rem;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li {
    margin-bottom: 0.25rem;
  }
}

::v-deep .editor-content-large .ProseMirror {
  min-height: 260px;
}

/*
|--------------------------------------------------------------------------
| Responsive
|--------------------------------------------------------------------------
*/

@media screen and (max-width: 768px) {
  #landing-plan-form-page {
    padding: 1rem 0.75rem 4rem;

    .form-container {
      max-width: none;
    }

    .page-header {
      flex-direction: column;
      min-height: auto;
    }

    .form-section {
      padding: 1rem;
    }

    .benefit-input-row {
      flex-direction: column;

      .button {
        width: 100%;
      }
    }

    .switch-row {
      align-items: flex-start;
    }

    .form-actions {
      flex-direction: column;

      .button {
        width: 100%;
      }
    }

    .plan-upload-button {
      padding-right: 0.75rem;
      padding-left: 0.75rem;
    }

    .editor-toolbar {
      gap: 0.25rem;
      padding: 0.45rem;

      .button {
        min-width: 32px;
      }
    }

    .editor-content {
      min-height: 180px;
    }
  }

  ::v-deep .editor-content .ProseMirror {
    min-height: 180px;
    padding: 0.85rem;
  }

  ::v-deep .editor-content-large .ProseMirror {
    min-height: 220px;
  }
}
</style>
